import { useState, useEffect } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RequestStep, StepOption, StepChips, StepProgress } from "@/components/ui/request-step";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import SEOHead from "@/components/seo/SEOHead";
import { useQuery } from "@tanstack/react-query";

type Step = "company" | "trades" | "region" | "radius" | "limit" | "complete";
const stepOrder: Step[] = ["company", "trades", "region", "radius", "limit", "complete"];

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { profile, createProfile, isLoadingProfile } = useProvider();

  const progressLabels = [t("onboarding.progressCompany"), t("onboarding.progressTradesRegion"), t("onboarding.progressConfig")];

  const limitOptions = [
    { id: "3", label: "3 " + t("onboarding.summaryPerWeek"), description: t("onboarding.limitFocused") },
    { id: "5", label: "5 " + t("onboarding.summaryPerWeek"), description: t("onboarding.limitStandard") },
    { id: "10", label: "10 " + t("onboarding.summaryPerWeek"), description: t("onboarding.limitIntensive") },
  ];

  const radiusOptions = [
    { id: "10", label: "10 km", description: t("onboarding.veryNear") },
    { id: "25", label: "25 km", description: t("onboarding.nearCity") },
    { id: "50", label: "50 km", description: t("onboarding.regional") },
    { id: "100", label: "100 km", description: t("onboarding.wide") },
    { id: "200", label: "200 km", description: t("onboarding.superRegional") },
  ];

  const { data: craftTrades = [] } = useQuery({
    queryKey: ["craft-trades"],
    queryFn: async () => {
      const { data } = await supabase
        .from("craft_trades")
        .select("id, key, label")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const [currentStep, setCurrentStep] = useState<Step>("company");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [isMasterCraftsman, setIsMasterCraftsman] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [region, setRegion] = useState("");
  const [serviceRadius, setServiceRadius] = useState("50");
  const [weeklyLimit, setWeeklyLimit] = useState("5");

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (profile?.onboarding_completed) navigate("/dashboard");
  }, [profile, navigate]);

  const currentIndex = stepOrder.indexOf(currentStep);
  const progressIndex = currentIndex <= 1 ? 0 : currentIndex <= 3 ? 1 : 2;

  const goNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < stepOrder.length) setCurrentStep(stepOrder[nextIndex]);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      if (!profile) {
        await createProfile.mutateAsync({
          company_name: companyName,
          contact_name: contactName,
          region: region || undefined,
          remote_supported: false,
          sla_response_hours: 24,
          weekly_lead_limit: parseInt(weeklyLimit),
        });
      }

      const { data: newProfile } = await supabase
        .from("provider_profiles")
        .select("id")
        .eq("user_id", user?.id)
        .single();

      if (!newProfile) throw new Error("Profil nicht gefunden");

      await supabase
        .from("provider_profiles")
        .update({
          onboarding_completed: true,
          is_master_craftsman: isMasterCraftsman,
          service_radius_km: parseInt(serviceRadius),
          craft_trades: selectedTrades,
          provider_type: "craft",
        })
        .eq("id", newProfile.id);

      toast.success(t("onboarding.profileCreated"));
      navigate("/dashboard");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error(t("onboarding.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || isLoadingProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Onboarding" noindex canonical="/onboarding" />
      <div className="fixed top-0 left-0 right-0 bg-background/80 backdrop-blur-sm z-40">
        <div className="section-padding py-6">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <span className="text-subheadline">CRAFTFINDUS</span>
            <StepProgress steps={progressLabels} currentIndex={progressIndex} />
          </div>
        </div>
      </div>

      <main className="min-h-screen flex items-center justify-center section-padding pt-24 pb-12">
        <div className="w-full max-w-xl">
          {currentStep === "company" && (
            <RequestStep key="company" title={t("onboarding.companyTitle")} subtitle={t("onboarding.companySubtitle")}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-micro block">{t("onboarding.companyName")}</label>
                  <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full h-12 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
                    placeholder={t("onboarding.companyNamePlaceholder")} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("onboarding.contactPerson")}</label>
                  <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                    className="w-full h-12 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
                    placeholder={t("onboarding.contactPersonPlaceholder")} />
                </div>
                <button
                  onClick={() => setIsMasterCraftsman(!isMasterCraftsman)}
                  className={`w-full text-left p-5 border transition-all duration-300 ${
                    isMasterCraftsman ? "border-accent bg-accent/5" : "border-border hover:border-muted-foreground"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-body font-medium">{t("onboarding.masterCraftsman")}</p>
                      <p className="text-caption mt-1">{t("onboarding.masterCraftsmanDesc")}</p>
                    </div>
                    {isMasterCraftsman && <div className="w-2 h-2 bg-accent rounded-full" />}
                  </div>
                </button>
                <Button variant="commit" size="lg" className="w-full mt-4" onClick={goNext}
                  disabled={!companyName.trim() || !contactName.trim()}>
                  {t("common.next")}
                </Button>
              </div>
            </RequestStep>
          )}

          {currentStep === "trades" && (
            <RequestStep key="trades" title={t("onboarding.tradesTitle")} subtitle={t("onboarding.tradesSubtitle")}>
              <div className="space-y-4">
                <StepChips
                  options={craftTrades.map(tr => ({ id: tr.key, label: tr.label }))}
                  selected={selectedTrades}
                  onToggle={(id) => setSelectedTrades(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                />
                <Button variant="commit" size="lg" className="w-full" onClick={goNext} disabled={selectedTrades.length === 0}>
                  {t("common.next")}
                </Button>
              </div>
            </RequestStep>
          )}

          {currentStep === "region" && (
            <RequestStep key="region" title={t("onboarding.regionTitle")} subtitle={t("onboarding.regionSubtitle")}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-micro block">{t("onboarding.regionLabel")}</label>
                  <input type="text" value={region} onChange={(e) => setRegion(e.target.value)}
                    className="w-full h-12 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
                    placeholder={t("onboarding.regionPlaceholder")} />
                </div>
                <Button variant="commit" size="lg" className="w-full" onClick={goNext}>{t("common.next")}</Button>
              </div>
            </RequestStep>
          )}

          {currentStep === "radius" && (
            <RequestStep key="radius" title={t("onboarding.radiusTitle")} subtitle={t("onboarding.radiusSubtitle")}>
              <div className="space-y-4">
                {radiusOptions.map((option) => (
                  <StepOption key={option.id} label={option.label} description={option.description}
                    isSelected={serviceRadius === option.id}
                    onClick={() => { setServiceRadius(option.id); setTimeout(goNext, 300); }} />
                ))}
              </div>
            </RequestStep>
          )}

          {currentStep === "limit" && (
            <RequestStep key="limit" title={t("onboarding.limitTitle")} subtitle={t("onboarding.limitSubtitle")}>
              <div className="space-y-4">
                {limitOptions.map((option) => (
                  <StepOption key={option.id} label={option.label} description={option.description}
                    isSelected={weeklyLimit === option.id}
                    onClick={() => { setWeeklyLimit(option.id); setTimeout(goNext, 300); }} />
                ))}
              </div>
            </RequestStep>
          )}

          {currentStep === "complete" && (
            <RequestStep key="complete" title={t("onboarding.completeTitle")} subtitle="">
              <div className="space-y-8">
                <div className="p-6 border border-border bg-card">
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-caption">{t("onboarding.summaryCompany")}</span>
                      <span className="text-body">{companyName}</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("onboarding.summaryTrades")}</span>
                      <span className="text-body">{selectedTrades.length} {t("common.selected")}</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("onboarding.summaryMaster")}</span>
                      <span className="text-body">{isMasterCraftsman ? t("common.yes") : t("common.no")}</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("onboarding.summaryRadius")}</span>
                      <span className="text-body">{serviceRadius} km</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("onboarding.summaryLimit")}</span>
                      <span className="text-body">{weeklyLimit} {t("onboarding.summaryPerWeek")}</span>
                    </div>
                  </div>
                </div>

                <Button variant="commit" size="xl" className="w-full" onClick={handleComplete} disabled={isSubmitting}>
                  {isSubmitting ? t("onboarding.saving") : t("onboarding.saveProfile")}
                </Button>
              </div>
            </RequestStep>
          )}
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
